import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, X, UserPlus, AlertTriangle } from 'lucide-react';
import { API_BASE, renderOptions, capitalizeFirst, AssigneeMultiSelect } from './helpers';

// Multi-property control for the lead form. The picker pool is all properties
// (GET /properties — the same source the Detail modal uses).
//
// Edit mode (leadId set): links are read from and written to the lead
// immediately through the existing /leads/:id/properties endpoints — the same
// proven path the Detail modal uses.
//
// Create mode (no leadId yet): the lead row doesn't exist, so links can't be
// written. Picks are held locally and reported via onSelectedChange; App's
// handleSave links them once the lead has been created.
function LeadPropertyMultiSelect({ leadId, selected, onSelectedChange }) {
  const [allProperties, setAllProperties] = useState([]);
  const [linked, setLinked] = useState([]);
  const [addId, setAddId] = useState('');
  const [loading, setLoading] = useState(false);
  const isEdit = !!leadId;

  // Pool to pick from — all properties.
  useEffect(() => {
    axios.get(`${API_BASE}/properties`)
      .then(res => setAllProperties(res.data))
      .catch(() => setAllProperties([]));
  }, []);

  // Edit mode: load the lead's currently-linked properties.
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    axios.get(`${API_BASE}/leads/${leadId}/properties`)
      .then(res => setLinked(res.data))
      .catch(() => setLinked([]))
      .finally(() => setLoading(false));
  }, [leadId, isEdit]);

  const byName = (a, b) => (a.PropertyName || '').localeCompare(b.PropertyName || '');
  const current = isEdit ? linked : (selected || []);
  const currentIds = new Set(current.map(p => p.PropertyId));
  const available = allProperties.filter(p => !currentIds.has(p.PropertyId)).sort(byName);

  const addProperty = async () => {
    if (!addId) return;
    const pid = Number(addId);
    const prop = allProperties.find(p => p.PropertyId === pid);
    if (!prop) return;
    if (isEdit) {
      try {
        await axios.post(`${API_BASE}/leads/${leadId}/properties`, { propertyId: pid });
        setLinked(prev => [...prev, prop].sort(byName));
        setAddId('');
      } catch (err) {
        if (err.response && err.response.status === 409) {
          // Already linked (e.g. via the Detail modal) — resync from the server.
          axios.get(`${API_BASE}/leads/${leadId}/properties`).then(res => setLinked(res.data)).catch(() => {});
          setAddId('');
        } else {
          console.error('Error linking property:', err);
        }
      }
    } else {
      onSelectedChange([...current, prop].sort(byName));
      setAddId('');
    }
  };

  const removeProperty = async (pid) => {
    if (isEdit) {
      try {
        await axios.delete(`${API_BASE}/leads/${leadId}/properties/${pid}`);
        setLinked(prev => prev.filter(p => p.PropertyId !== pid));
      } catch (err) {
        console.error('Error unlinking property:', err);
      }
    } else {
      onSelectedChange(current.filter(p => p.PropertyId !== pid));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: current.length ? '0.6rem' : 0 }}>
        <select
          value={addId}
          onChange={(e) => setAddId(e.target.value)}
          disabled={loading}
          className="crm-form__select"
          style={{ flex: 1, minWidth: 0 }}
        >
          <option value="">{loading ? 'Loading properties...' : 'Add a property...'}</option>
          {available.map(p => (
            <option key={p.PropertyId} value={p.PropertyId}>
              {p.PropertyName}{p.YardiCode ? ` [${p.YardiCode}]` : ''}{p.PropertyAddress ? ` — ${p.PropertyAddress}` : ''}{p.City ? `, ${p.City}` : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={addProperty}
          disabled={!addId}
          className="crm-btn crm-btn--primary"
          style={{ flexShrink: 0 }}
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {current.length === 0 ? (
        <p className="crm-text--muted" style={{ fontSize: '0.813rem', margin: 0 }}>No properties linked yet</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {current.map(p => (
            <span key={p.PropertyId} className="crm-badge crm-badge--blue" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {p.PropertyName}{p.YardiCode ? ` [${p.YardiCode}]` : ''}
              <button
                type="button"
                onClick={() => removeProperty(p.PropertyId)}
                className="crm-btn crm-btn--secondary crm-btn--icon"
                style={{ padding: '0 2px' }}
                data-tooltip="Remove"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Form Modal Component
function FormModal({ type, item, defaultData, onClose, onSave, dropdownOptions, contacts, entities, users, onContactCreated }) {
  // Initialize form data, mapping database PascalCase to API camelCase
  const [formData, setFormData] = useState(() => {
    if (!item) return defaultData || {};
    
    // Map database column names to API field names
    const mapped = {};
    Object.keys(item).forEach(key => {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      mapped[camelKey] = item[key];
    });
    
    // Format dates to YYYY-MM-DD for date inputs
    if (mapped.dueDate) {
      mapped.dueDate = mapped.dueDate.split('T')[0];
    }
    if (mapped.nextFollowUpDate) {
      mapped.nextFollowUpDate = mapped.nextFollowUpDate.split('T')[0];
    }
    if (mapped.startDate) {
      mapped.startDate = mapped.startDate.split('T')[0];
    }
    if (mapped.endDate) {
      mapped.endDate = mapped.endDate.split('T')[0];
    }
    if (mapped.lastContactDate) {
      mapped.lastContactDate = mapped.lastContactDate.split('T')[0];
    }
    if (mapped.signedDate) {
      mapped.signedDate = mapped.signedDate.split('T')[0];
    }
    if (mapped.acquisitionDate) {
      mapped.acquisitionDate = mapped.acquisitionDate.split('T')[0];
    }
    if (mapped.renovationDate) {
      mapped.renovationDate = mapped.renovationDate.split('T')[0];
    }

    // Reminder multi-assignee: derive the id array from the Assignees JSON
    // (new shape) or the legacy single AssignedTo.
    if (type === 'reminder') {
      if (mapped.assignees !== undefined && mapped.assignees !== null) {
        let parsedAssignees = [];
        if (Array.isArray(mapped.assignees)) parsedAssignees = mapped.assignees;
        else if (typeof mapped.assignees === 'string' && mapped.assignees.trim()) {
          try { parsedAssignees = JSON.parse(mapped.assignees) || []; } catch { parsedAssignees = []; }
        }
        mapped.assignedToUserIds = parsedAssignees.map(a => a.UserId);
      } else if (mapped.assignedTo) {
        mapped.assignedToUserIds = [mapped.assignedTo];
      }
    }

    return mapped;
  });

  const [showQuickContact, setShowQuickContact] = useState(false);
  const [quickContact, setQuickContact] = useState({ firstName: '', lastName: '', companyName: '', email: '', phone: '' });
  const [localContacts, setLocalContacts] = useState(contacts);
  const [quickContactError, setQuickContactError] = useState('');

  const [quickContactDuplicates, setQuickContactDuplicates] = useState(null);

  // Guards setState after the quick-contact async chain when the modal
  // unmounts mid-request (e.g. user cancels while the save is in flight).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleQuickContactSave = async (force) => {
    if (!quickContact.firstName.trim() || !quickContact.lastName.trim()) return;
    setQuickContactError('');
    try {
      // Check for duplicates first (unless forcing)
      if (!force) {
        const checkRes = await axios.post(`${API_BASE}/contacts/check-duplicate`, quickContact);
        if (!mountedRef.current) return;
        if (checkRes.data.duplicates && checkRes.data.duplicates.length > 0) {
          setQuickContactDuplicates(checkRes.data.duplicates);
          return;
        }
      }
      setQuickContactDuplicates(null);
      const res = await axios.post(`${API_BASE}/contacts`, quickContact);
      const newContactId = res.data.contactId;
      const updatedContacts = await onContactCreated();
      if (!mountedRef.current) return;
      setLocalContacts(updatedContacts);
      setFormData(prev => ({ ...prev, contactId: String(newContactId) }));
      setShowQuickContact(false);
      setQuickContact({ firstName: '', lastName: '', companyName: '', email: '', phone: '' });
    } catch (err) {
      if (!mountedRef.current) return;
      setQuickContactError(err.response?.data?.error || err.message);
    }
  };

  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Flips true on the first user-driven edit to any form field. Gates the
  // discard-confirm on every close path (Escape, overlay, X, Cancel).
  // Mirrors the guard in DMCreateEnvelopeModal.
  const [dirty, setDirty] = useState(false);
  // True only when a backdrop press ORIGINATED on the backdrop. A
  // text-selection drag that starts inside an input and releases on the
  // backdrop must not be treated as a click-outside dismiss.
  const overlayPressOnBackdrop = useRef(false);

  // Never discard in-progress work without warning. A clean form
  // (dirty === false) still closes silently, exactly as before.
  const requestClose = () => {
    if (dirty) {
      const msg = item
        ? "Discard your unsaved changes? You'll lose any edits since the last save."
        : `Discard this new ${type}? Your changes will be lost.`;
      if (!window.confirm(msg)) return;
    }
    onClose();
  };

  // Close on Escape, through the same dirty guard as every other close path.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, item, type, onClose]);

  const handleOverlayMouseDown = (e) => {
    overlayPressOnBackdrop.current = e.target === e.currentTarget;
  };
  const handleOverlayClick = (e) => {
    if (e.target !== e.currentTarget) return;     // click resolved to a child
    if (!overlayPressOnBackdrop.current) return;  // press began inside the modal
    overlayPressOnBackdrop.current = false;
    requestClose();
  };

  // Guards rapid double-clicks on Save from firing duplicate POSTs.
  // Mirrors the submitting guard in SignPage/DMCreateEnvelopeModal.
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      // Only check duplicates on create (not edit) for contacts and properties
      if (!item && (type === 'contact' || type === 'property')) {
        try {
          const endpoint = type === 'property' ? 'properties' : 'contacts';
          const res = await axios.post(`${API_BASE}/${endpoint}/check-duplicate`, formData);
          if (res.data.duplicates && res.data.duplicates.length > 0) {
            setDuplicateWarning(res.data.duplicates);
            return;
          }
        } catch (err) {
          // If check fails, proceed with save anyway
          console.error('Duplicate check failed:', err);
        }
      }

      await onSave(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAnyway = async () => {
    if (submitting) return;
    setSubmitting(true);
    setDuplicateWarning(null);
    try {
      await onSave(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div
      className="crm-modal__overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__header">
          <h2 className="crm-modal__title">
            {item ? `Edit ${capitalizeFirst(type)}` : `Add New ${capitalizeFirst(type)}`}
          </h2>
                  <button onClick={requestClose} className="crm-btn crm-btn--secondary crm-btn--icon" data-tooltip="Close">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} onChange={() => setDirty(true)}>
          <div className="crm-modal__body">
            {/* CONTACT FORM */}
            {type === 'contact' && (
              <>
                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      required
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      required
                    />
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Company</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                  />
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Mobile Phone</label>
                  <input
                    type="text"
                    name="mobilePhone"
                    value={formData.mobilePhone || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                  />
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                  />
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Zip Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Type *</label>
                    <select
                      name="contactType"
                      value={formData.contactType || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                      required
                    >
                      {renderOptions(dropdownOptions.ContactType, 'Select...')}
                    </select>
                  </div>
                  {item && (
                    <div className="crm-form__group crm-form__col">
                      <label className="crm-form__label">Status</label>
                      <select
                        name="status"
                        value={formData.status || 'Active'}
                        onChange={handleChange}
                        className="crm-form__select"
                      >
                        {renderOptions(dropdownOptions.ContactStatus)}
                      </select>
                    </div>
                  )}
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Preferred Contact</label>
                    <select
                      name="preferredContactMethod"
                      value={formData.preferredContactMethod || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                    >
                      {renderOptions(dropdownOptions.PreferredContactMethod, 'Select...')}
                    </select>
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleChange}
                    className="crm-form__textarea"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* LEAD FORM */}
            {type === 'lead' && (
              <>
                <div className="crm-form__group">
                  <label className="crm-form__label">Contact *</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      name="contactId"
                      value={formData.contactId || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">Select contact...</option>
                      {(localContacts || []).map(c => (
                        <option key={c.ContactId} value={c.ContactId}>
                          {c.FirstName} {c.LastName}{c.CompanyName ? ` — ${c.CompanyName}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowQuickContact(prev => !prev)}
                      className="crm-btn crm-btn--secondary"
                      style={{ flexShrink: 0, padding: '0.5rem 0.75rem' }}
                      data-tooltip="New Contact"
                    >
                      <UserPlus size={18} />
                    </button>
                  </div>
                </div>

                {/* Quick Add Contact Sub-Form */}
                {showQuickContact && (
                  <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--background)', borderRadius: '8px', border: '1px solid var(--input-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                        <UserPlus size={14} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                        Quick Add Contact
                      </h4>
                      <button type="button" onClick={() => setShowQuickContact(false)} className="crm-btn crm-btn--secondary crm-btn--icon" style={{ padding: '2px' }}>
                        <X size={14} />
                      </button>
                    </div>
                    <div className="crm-form__row">
                      <div className="crm-form__group crm-form__col">
                        <label className="crm-form__label">First Name *</label>
                        <input
                          type="text"
                          value={quickContact.firstName}
                          onChange={(e) => setQuickContact(prev => ({ ...prev, firstName: e.target.value }))}
                          className="crm-form__input"
                        />
                      </div>
                      <div className="crm-form__group crm-form__col">
                        <label className="crm-form__label">Last Name *</label>
                        <input
                          type="text"
                          value={quickContact.lastName}
                          onChange={(e) => setQuickContact(prev => ({ ...prev, lastName: e.target.value }))}
                          className="crm-form__input"
                        />
                      </div>
                    </div>
                    <div className="crm-form__group" style={{ marginTop: '0.5rem' }}>
                      <label className="crm-form__label">Company</label>
                      <input
                        type="text"
                        value={quickContact.companyName}
                        onChange={(e) => setQuickContact(prev => ({ ...prev, companyName: e.target.value }))}
                        className="crm-form__input"
                      />
                    </div>
                    <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                      <div className="crm-form__group crm-form__col">
                        <label className="crm-form__label">Email</label>
                        <input
                          type="email"
                          value={quickContact.email}
                          onChange={(e) => setQuickContact(prev => ({ ...prev, email: e.target.value }))}
                          className="crm-form__input"
                        />
                      </div>
                      <div className="crm-form__group crm-form__col">
                        <label className="crm-form__label">Phone</label>
                        <input
                          type="text"
                          value={quickContact.phone}
                          onChange={(e) => setQuickContact(prev => ({ ...prev, phone: e.target.value }))}
                          className="crm-form__input"
                        />
                      </div>
                    </div>
                    {quickContactError && (
                      <p style={{ color: 'var(--danger)', fontSize: '0.813rem', marginTop: '0.5rem' }}>{quickContactError}</p>
                    )}
                    {quickContactDuplicates && (
                      <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '0.813rem', color: '#78350f' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                          <AlertTriangle size={14} color="#d97706" />
                          <strong style={{ color: '#92400e' }}>Possible duplicates:</strong>
                        </div>
                        {quickContactDuplicates.map(d => (
                          <div key={d.ContactId} style={{ padding: '0.25rem 0' }}>
                            <strong>{d.FirstName} {d.LastName}</strong>
                            {d.CompanyName && ` — ${d.CompanyName}`}
                            {d.Email && <span style={{ color: '#a16207' }}> ({d.Email})</span>}
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                          <button type="button" onClick={() => setQuickContactDuplicates(null)} className="crm-btn crm-btn--secondary" style={{ fontSize: '0.75rem', padding: '2px 8px' }}>
                            Go Back
                          </button>
                          <button type="button" onClick={() => handleQuickContactSave(true)} className="crm-btn crm-btn--primary" style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#d97706' }}>
                            Save Anyway
                          </button>
                        </div>
                      </div>
                    )}
                    {!quickContactDuplicates && (
                      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => handleQuickContactSave(false)}
                          className="crm-btn crm-btn--primary"
                          disabled={!quickContact.firstName.trim() || !quickContact.lastName.trim()}
                        >
                          <Plus size={16} /> Save & Select
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Source</label>
                    <select
                      name="source"
                      value={formData.source || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                    >
                      {renderOptions(dropdownOptions.LeadSource, 'Select...')}
                    </select>
                  </div>
                  {item && (
                    <div className="crm-form__group crm-form__col">
                      <label className="crm-form__label">Status</label>
                      <select
                        name="status"
                        value={formData.status || 'New'}
                        onChange={handleChange}
                        className="crm-form__select"
                      >
                        {renderOptions(dropdownOptions.LeadStatus)}
                      </select>
                    </div>
                  )}
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Linked Properties</label>
                  <LeadPropertyMultiSelect
                    leadId={item?.LeadId || null}
                    selected={formData.pendingProperties || []}
                    onSelectedChange={(props) => setFormData(prev => ({ ...prev, pendingProperties: props }))}
                  />
                  {!item && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
                      Properties are linked when the lead is created.
                    </p>
                  )}
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Property Interest (description)</label>
                  <textarea
                    name="propertyInterest"
                    value={formData.propertyInterest || ''}
                    onChange={handleChange}
                    className="crm-form__textarea"
                    rows={2}
                    placeholder="Describe the property or deal being discussed..."
                  />
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Estimated Value ($)</label>
                    <input
                      type="number"
                      name="estimatedValue"
                      value={formData.estimatedValue || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      min="0"
                      step="1000"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Follow-Up Date <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>→ creates reminder</span></label>
                    <input
                      type="date"
                      name="nextFollowUpDate"
                      value={formData.nextFollowUpDate || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Assigned To</label>
                  <select
                    name="assignedTo"
                    value={formData.assignedTo || ''}
                    onChange={handleChange}
                    className="crm-form__select"
                  >
                    <option value="">Select user...</option>
                    {(users || []).map(u => (
                      <option key={u.UserId} value={u.UserId}>
                        {u.FirstName} {u.LastName}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* REMINDER FORM */}
            {type === 'reminder' && (
              <>
                <div className="crm-form__group">
                  <label className="crm-form__label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                    required
                  />
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    className="crm-form__textarea"
                    rows={3}
                  />
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Type *</label>
                    <select
                      name="reminderType"
                      value={formData.reminderType || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                      required
                    >
                      {renderOptions(dropdownOptions.ReminderType, 'Select...')}
                    </select>
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Priority *</label>
                    <select
                      name="priority"
                      value={formData.priority || 'Medium'}
                      onChange={handleChange}
                      className="crm-form__select"
                      required
                    >
                      {renderOptions(dropdownOptions.ReminderPriority)}
                    </select>
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Due Date *</label>
                    <input
                      type="date"
                      name="dueDate"
                      value={formData.dueDate || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      required
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Due Time</label>
                    <input
                      type="time"
                      name="dueTime"
                      value={formData.dueTime || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Related To</label>
                    <select
                      name="relatedToType"
                      value={formData.relatedToType || ''}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, relatedToType: e.target.value, relatedToId: '' }));
                      }}
                      className="crm-form__select"
                    >
                      <option value="">None</option>
                      <option value="Contact">Contact</option>
                      <option value="Lead">Lead</option>
                      <option value="Property">Property</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">
                      {formData.relatedToType ? `Select ${formData.relatedToType}` : 'Select Entity'}
                    </label>
                    <select
                      name="relatedToId"
                      value={formData.relatedToId || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                      disabled={!formData.relatedToType}
                    >
                      <option value="">{formData.relatedToType ? `Choose a ${formData.relatedToType.toLowerCase()}...` : 'Select type first...'}</option>
                      {formData.relatedToType === 'Contact' && (entities.contacts || []).map(c => (
                        <option key={c.ContactId} value={c.ContactId}>
                          {c.FirstName} {c.LastName}{c.CompanyName ? ` — ${c.CompanyName}` : ''}
                        </option>
                      ))}
                      {formData.relatedToType === 'Lead' && (entities.leads || []).map(l => (
                        <option key={l.LeadId} value={l.LeadId}>
                          {l.ContactFirstName || l.FirstName} {l.ContactLastName || l.LastName} — {l.PropertyInterest || 'No interest noted'}
                        </option>
                      ))}
                      {formData.relatedToType === 'Property' && (entities.properties || []).map(p => (
                        <option key={p.PropertyId} value={p.PropertyId}>
                          {p.PropertyName}{p.PropertyAddress ? ` — ${p.PropertyAddress}` : ''}
                        </option>
                      ))}
                      {formData.relatedToType === 'Contract' && (entities.contracts || []).map(ct => (
                        <option key={ct.ContractId} value={ct.ContractId}>
                          {ct.ContractNumber}{ct.ContactName ? ` — ${ct.ContactName}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Assigned To</label>
                  <AssigneeMultiSelect
                    users={users}
                    value={formData.assignedToUserIds}
                    onChange={(ids) => { setFormData(prev => ({ ...prev, assignedToUserIds: ids })); setDirty(true); }}
                  />
                </div>
              </>
            )}

            {/* CONTRACT FORM */}
            {type === 'contract' && (
              <>
                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Contact *</label>
                    <select
                      name="contactId"
                      value={formData.contactId || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                      required
                    >
                      <option value="">Select contact...</option>
                      {(contacts || []).map(c => (
                        <option key={c.ContactId} value={c.ContactId}>
                          {c.FirstName} {c.LastName}{c.CompanyName ? ` — ${c.CompanyName}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Contract Number *</label>
                    <input
                      type="text"
                      name="contractNumber"
                      value={formData.contractNumber || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      required
                    />
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Type *</label>
                    <select
                      name="contractType"
                      value={formData.contractType || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                      required
                    >
                      {renderOptions(dropdownOptions.ContractType, 'Select...')}
                    </select>
                  </div>
                  {item && (
                    <div className="crm-form__group crm-form__col">
                      <label className="crm-form__label">Status</label>
                      <select
                        name="status"
                        value={formData.status || 'Draft'}
                        onChange={handleChange}
                        className="crm-form__select"
                      >
                        {renderOptions(dropdownOptions.ContractStatus)}
                      </select>
                    </div>
                  )}
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Value ($)</label>
                  <input
                    type="number"
                    name="value"
                    value={formData.value || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                    min="0"
                    step="1000"
                  />
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Property Address</label>
                  <input
                    type="text"
                    name="propertyAddress"
                    value={formData.propertyAddress || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                  />
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Terms</label>
                  <textarea
                    name="terms"
                    value={formData.terms || ''}
                    onChange={handleChange}
                    className="crm-form__textarea"
                    rows={3}
                  />
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleChange}
                    className="crm-form__textarea"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* PROPERTY FORM */}
            {type === 'property' && (
              <>
                <div className="crm-form__group">
                  <label className="crm-form__label">Initial Owner (Contact)</label>
                  <select
                    name="contactId"
                    value={formData.contactId || ''}
                    onChange={handleChange}
                    className="crm-form__select"
                  >
                    <option value="">No owner (add later)...</option>
                    {(contacts || []).map(c => (
                      <option key={c.ContactId} value={c.ContactId}>
                        {c.FirstName} {c.LastName}{c.CompanyName ? ` — ${c.CompanyName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Property Name *</label>
                    <input
                      type="text"
                      name="propertyName"
                      value={formData.propertyName || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      required
                    />
                  </div>
                  <div className="crm-form__group crm-form__col" style={{ maxWidth: '140px' }}>
                    <label className="crm-form__label">Yardi Code</label>
                    <input
                      type="text"
                      name="yardiCode"
                      value={formData.yardiCode || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      maxLength={8}
                      placeholder="e.g. PROP0001"
                    />
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Property Address</label>
                  <input
                    type="text"
                    name="propertyAddress"
                    value={formData.propertyAddress || ''}
                    onChange={handleChange}
                    className="crm-form__input"
                  />
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Zip Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Property Type</label>
                    <select
                      name="propertyType"
                      value={formData.propertyType || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                    >
                      {renderOptions(dropdownOptions.PropertyType, 'Select type...')}
                    </select>
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Status</label>
                    <select
                      name="status"
                      value={formData.status || ''}
                      onChange={handleChange}
                      className="crm-form__select"
                    >
                      <option value="">Select...</option>
                      <option value="Under Construction">Under Construction</option>
                      <option value="Under Renovation">Under Renovation</option>
                      <option value="Vacant-Lease Up">Vacant-Lease Up</option>
                      <option value="Vacant-Distressed">Vacant-Distressed</option>
                      <option value="Stabilized">Stabilized</option>
                    </select>
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Units</label>
                    <input
                      type="number"
                      name="units"
                      value={formData.units || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Market Value ($)</label>
                    <input
                      type="number"
                      name="marketValue"
                      value={formData.marketValue || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Year Built</label>
                    <input
                      type="number"
                      name="yearBuilt"
                      value={formData.yearBuilt || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      min="1800"
                      max="2099"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Acquisition Date</label>
                    <input
                      type="date"
                      name="acquisitionDate"
                      value={formData.acquisitionDate || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Renovation Date</label>
                    <input
                      type="date"
                      name="renovationDate"
                      value={formData.renovationDate || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                    />
                  </div>
                </div>

                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Monthly Mgmt Fee ($)</label>
                    <input
                      type="number"
                      name="monthlyManagementFee"
                      value={formData.monthlyManagementFee || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      step="0.01"
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Monthly Revenue ($)</label>
                    <input
                      type="number"
                      name="monthlyRevenue"
                      value={formData.monthlyRevenue || ''}
                      onChange={handleChange}
                      className="crm-form__input"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="crm-form__group">
                  <label className="crm-form__label">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleChange}
                    className="crm-form__textarea"
                    rows={3}
                  />
                </div>
              </>
            )}
          </div>

          {/* Duplicate Warning */}
          {duplicateWarning && (
            <div style={{ margin: '0 1.5rem 1rem', padding: '1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <AlertTriangle size={18} color="#d97706" />
                <strong style={{ color: '#92400e', fontSize: '0.875rem' }}>Possible Duplicates Found</strong>
              </div>
              <div style={{ fontSize: '0.813rem', color: '#78350f' }}>
                {type === 'contact' && duplicateWarning.map(d => (
                  <div key={d.ContactId} style={{ padding: '0.4rem 0', borderBottom: '1px solid #fde68a' }}>
                    <strong>{d.FirstName} {d.LastName}</strong>
                    {d.CompanyName && <span> — {d.CompanyName}</span>}
                    {d.Email && <span style={{ marginLeft: '0.5rem', color: '#a16207' }}>({d.Email})</span>}
                  </div>
                ))}
                {type === 'property' && duplicateWarning.map(d => (
                  <div key={d.PropertyId} style={{ padding: '0.4rem 0', borderBottom: '1px solid #fde68a' }}>
                    <strong>{d.PropertyName}</strong>
                    {d.PropertyAddress && <span> — {d.PropertyAddress}</span>}
                    {d.YardiCode && <span style={{ marginLeft: '0.5rem', fontFamily: 'monospace', color: '#a16207' }}>({d.YardiCode})</span>}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setDuplicateWarning(null)} className="crm-btn crm-btn--secondary" style={{ fontSize: '0.813rem' }}>
                  Go Back & Edit
                </button>
                <button type="button" onClick={handleSaveAnyway} disabled={submitting} className="crm-btn crm-btn--primary" style={{ fontSize: '0.813rem', background: '#d97706' }}>
                  {submitting ? 'Saving…' : 'Save Anyway'}
                </button>
              </div>
            </div>
          )}
          
          <div className="crm-modal__footer">
            <button type="button" onClick={requestClose} className="crm-btn crm-btn--secondary">
              Cancel
            </button>
            <button type="submit" className="crm-btn crm-btn--primary" disabled={submitting}>
              {submitting ? 'Saving…' : `Save ${capitalizeFirst(type)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Detail Modal Component

export default FormModal;
